import React, { useEffect, useState } from 'react'
import { Dialog, Typography, IconButton, Box, Button, Divider, TextField, Chip, InputBase } from '@mui/material'

import { ReactComponent as CloseIcon } from '../../../../assets/images/Close.svg'
import { ReactComponent as AddIcon } from '../../../../assets/images/add-circle.svg'

import { useTppData } from '../../hooks/useTppData'
import { useTppMutations } from '../../hooks/useTppMutations'

import { useUpdateColumnContentsMutation } from '../../../../views/TPP/components/Column/queries/useUpdateColumnContentsMutation'
import SectionEditComponent from '../../../../views/TPP/components/Column/components/SectionEditComponent'
import { useIsMobileDevice } from '../../../../shared/hooks/useIsMobileDevice'

export interface ColumnEditViewProps {
    columnId: string
    tppId: string
    open: boolean
    onClose: () => void
}

const ColumnEditView: React.FC<ColumnEditViewProps> = ({ tppId, columnId, open, onClose }) => {
    const { matrixState, isLoading, error } = useTppData(tppId)
    const { createSlot } = useTppMutations(tppId)

    const effectiveColumnId = columnId

    const columns = React.useMemo(() => (matrixState ? Object.values(matrixState.columns) : []), [matrixState])

    const currentColumn = React.useMemo(
        () => columns.find((col: any) => col._id === effectiveColumnId) ?? null,
        [columns, effectiveColumnId]
    )

    const tpp = matrixState?.tpp

    const sections = React.useMemo(() => (matrixState ? Object.values(matrixState.sections) : []), [matrixState])

    const rows = React.useMemo(() => (matrixState ? Object.values(matrixState.rows) : []), [matrixState])

    const cellsForColumn = React.useMemo(() => {
        if (!matrixState) return []
        const result: any[] = []
        Object.values(matrixState.cellLookup).forEach((slotMap: any) => {
            Object.values(slotMap).forEach((cell: any) => {
                if (cell.columnId === effectiveColumnId) {
                    result.push(cell)
                }
            })
        })
        return result
    }, [matrixState, effectiveColumnId])

    const existingCellIds = React.useMemo(() => new Set(cellsForColumn.map((cell: any) => cell._id)), [cellsForColumn])

    const existingCellById = React.useMemo(() => {
        const m = new Map<string, any>()
        cellsForColumn.forEach((c: any) => {
            if (c?._id) m.set(String(c._id), c)
        })
        return m
    }, [cellsForColumn])

    const orderedSections = React.useMemo(() => {
        if (!sections) return []

        const sectionIdsFromTPP = Array.isArray(tpp?.sectionIds) ? tpp.sectionIds : null

        if (!sectionIdsFromTPP) {
            return sections
        }

        const orderedIds = sectionIdsFromTPP.map((id: any) => {
            if (typeof id === 'string') return id
            if (id && typeof id === 'object' && '_id' in id) {
                return (id as any)._id
            }
            return String(id)
        })

        const sectionMap = new Map(sections.map((s) => [s._id, s]))

        const result: typeof sections = []

        // Push sections in the order defined by TPP.sectionIds
        orderedIds.forEach((id) => {
            const section = sectionMap.get(id)
            if (section) {
                result.push(section)
                sectionMap.delete(id)
            }
        })

        // Append any remaining sections not listed in sectionIds
        sectionMap.forEach((section) => {
            result.push(section)
        })

        return result
    }, [sections, tpp])

    const [editableName, setEditableName] = useState<string>('')
    const [editableTags, setEditableTags] = useState<string[]>([])
    const [isAddingTag, setIsAddingTag] = useState(false)
    const [newTagInput, setNewTagInput] = useState('')

    const [editedCells, setEditedCells] = useState<Record<string, string>>({})
    const [deletedCellIds, setDeletedCellIds] = useState<string[]>([])

    const updateColumnContentsMutation = useUpdateColumnContentsMutation()

    const handleCellChange = (cellId: string, value: string) => {
        setEditedCells((prev) => ({
            ...prev,
            [cellId]: value,
        }))
    }

    const handleCellDelete = (cellId: string) => {
        setDeletedCellIds((prev) => (prev.includes(cellId) ? prev : [...prev, cellId]))
        // Drop any pending edits for the deleted cell
        setEditedCells((prev) => {
            const next = { ...prev }
            delete next[cellId]
            return next
        })
    }

    const handleSave = async () => {
        if (!currentColumn) {
            onClose()
            return
        }

        try {
            // Arrays shaped to match the Zod schemas:
            // UpdatePayload: { cells?: UpdateCellSchema[], columns?: UpdateColumnSchema[] }
            // CreatePayload: { cells?: CreateCellSchema[] }
            // DeletePayload: { cells?: DeleteCellSchema[] }
            const updateCells: { _id: string; content: string; columnId: string; slotId: string }[] = []
            const createCells: { tppId: string; columnId: string; slotId: string; content: string }[] = []
            const deleteCells: { _id: string }[] = []
            const updateColumns: { title: string; keyMarkets?: string[] }[] = []

            // Column-level updates (name + tags)
            const originalName = currentColumn.title ?? ''
            const originalTags = Array.isArray(currentColumn.keyMarkets) ? currentColumn.keyMarkets.map(String) : []

            const nameChanged = editableName !== originalName
            const tagsChanged =
                editableTags.length !== originalTags.length || editableTags.some((tag, i) => tag !== originalTags[i])

            if (nameChanged || tagsChanged) {
                updateColumns.push({
                    title: editableName,
                    keyMarkets: editableTags,
                })
            }

            // First pass: existing cell updates, collect new cells for creation
            const newCellsToCreate: Array<{ cellId: string; content: string }> = []

            Object.entries(editedCells).forEach(([cellId, content]) => {
                // Skip if this cell is also deleted
                if (deletedCellIds.includes(cellId)) return

                if (existingCellIds.has(cellId)) {
                    // Existing cell → include slotId from the existing cell
                    const existingCell = existingCellById.get(String(cellId))

                    if (!existingCell?.slotId) {
                        console.warn('Missing slotId on existing cell; skipping update for cell', {
                            cellId,
                            existingCell,
                        })
                        return
                    }

                    updateCells.push({
                        _id: cellId,
                        content,
                        columnId: String(effectiveColumnId),
                        slotId: String(existingCell.slotId),
                    })
                } else {
                    // New cell; handle in second pass so we can await slot creation
                    newCellsToCreate.push({ cellId, content })
                }
            })

            for (const { cellId, content } of newCellsToCreate) {
                let rowId: string | null = null
                if (cellId.startsWith('new-')) {
                    const parts = cellId.split('-')
                    if (parts.length >= 3) {
                        // ["new", "{rowId}", ...]
                        rowId = parts[1]
                    }
                }

                if (!rowId) {
                    console.warn('Unable to infer rowId from new cell id; skipping create for cell', cellId)
                    continue
                }

                const slotResponse: any = await createSlot.mutateAsync({
                    rowId,
                })

                // Shapes we support:
                // 1) { cell, slot, row }
                // 2) { success, data: { cell, slot, row } }
                const raw = slotResponse?.data ?? slotResponse
                const slotObj = raw?.slot ?? raw
                const createdCell = raw?.cell
                const slotId: string | undefined = slotObj?._id ?? slotObj?.id

                if (!slotId) {
                    console.error('No slotId returned from createTPPSlotMutation; skipping new cell', {
                        cellId,
                        slotResponse,
                    })
                    continue
                }

                const firstColumnIdFromTPP =
                    Array.isArray(tpp?.columnIds) && tpp.columnIds.length > 0 ? String(tpp.columnIds[0]) : null
                const effectiveColumnIdStr = effectiveColumnId ? String(effectiveColumnId) : null

                if (
                    firstColumnIdFromTPP &&
                    effectiveColumnIdStr &&
                    firstColumnIdFromTPP === effectiveColumnIdStr &&
                    createdCell?.['_id']
                ) {
                    // We are editing the FIRST column, and create
                    // Slot already created a cell for this column.
                    // So we should UPDATE that cell, not CREATE a new one.
                    const existingCell = existingCellById.get(String(cellId))
                    updateCells.push({
                        _id: createdCell._id,
                        content,
                        columnId: effectiveColumnId,
                        slotId: String(existingCell.slotId),
                    })
                } else {
                    // We are editing some other column – createSlot only created the first-column cell.
                    // It is safe to CREATE a new cell for (columnId, slotId) here.
                    createCells.push({
                        tppId,
                        columnId: effectiveColumnId,
                        slotId,
                        content,
                    })
                }
            }
            deletedCellIds.forEach((cellId) => {
                if (!existingCellIds.has(cellId)) {
                    return
                }
                deleteCells.push({ _id: cellId })
            })

            const operations: any[] = []

            // UPDATE operation (columns and/or cells)
            if (updateColumns.length || updateCells.length) {
                operations.push({
                    action: 'update',
                    payload: {
                        columns: updateColumns.length ? updateColumns : undefined,
                        cells: updateCells.length ? updateCells : undefined,
                    },
                })
            }

            // CREATE operation (cells) – now has tppId, columnId, slotId, content
            if (createCells.length) {
                operations.push({
                    action: 'create',
                    payload: {
                        cells: createCells,
                    },
                })
            }

            // DELETE operation (cells)
            if (deleteCells.length) {
                operations.push({
                    action: 'delete',
                    payload: {
                        cells: deleteCells,
                    },
                })
            }

            if (operations.length === 0) {
                onClose()
                return
            }

            await updateColumnContentsMutation.mutateAsync({
                columnId: effectiveColumnId,
                tppId,
                operations,
            })

            onClose()
        } catch (e) {
            console.error('Error saving column contents', e)
        }
    }

    const handleCancel = () => {
        // If you want to also reset local edits, you could clear state here.
        // For now, just close the dialog.
        onClose()
    }

    useEffect(() => {
        if (currentColumn) {
            setEditableName(currentColumn.title ?? '')
            const km = Array.isArray(currentColumn.keyMarkets) ? currentColumn.keyMarkets : []
            setEditableTags(km.map((v) => String(v)))
        }
    }, [currentColumn])

    const handleRemoveTag = (tagToRemove: string) => {
        setEditableTags((prev) => prev.filter((tag) => tag !== tagToRemove))
    }

    const handleAddTag = () => {
        setIsAddingTag(true)
    }

    const commitNewTag = () => {
        const trimmed = newTagInput.trim()
        if (!trimmed) {
            setNewTagInput('')
            setIsAddingTag(false)
            return
        }

        setEditableTags((prev) => {
            if (prev.includes(trimmed)) {
                return prev
            }
            return [...prev, trimmed]
        })
        setNewTagInput('')
        setIsAddingTag(false)
    }

    const handleNewTagKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault()
            commitNewTag()
        } else if (event.key === 'Escape') {
            event.preventDefault()
            setNewTagInput('')
            setIsAddingTag(false)
        }
    }

    const handleNewTagBlur = () => {
        commitNewTag()
    }

    const isMobileDevice = useIsMobileDevice()

    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullScreen={isMobileDevice}
            maxWidth="md"
            fullWidth={false}
            BackdropProps={{
                sx: {
                    backgroundColor: 'rgba(0, 0, 0, 0.32)',
                },
            }}
            PaperProps={{
                sx: {
                    display: 'inline-flex',
                    p: 3,
                    pl: isMobileDevice ? 3 : 8,
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    borderRadius: '24px',
                    bgcolor: 'figma.grayscale.contentWrapper',
                },
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    alignSelf: 'stretch',
                    gap: 2,
                }}
            >
                <Typography
                    variant="22x600"
                    sx={{
                        textAlign: 'left',
                        color: 'figma.grayscale.textHier1',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 22,
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '150%', // 33px
                    }}
                >
                    Editing Asset Profile
                </Typography>
                <IconButton
                    aria-label="Close"
                    onClick={onClose}
                    sx={{
                        padding: 0,
                        color: 'figma.grayscale.textHier1',
                    }}
                >
                    <CloseIcon color="inherit" style={{ width: 24, height: 24 }} />
                </IconButton>
            </Box>
            <Divider
                sx={{
                    alignSelf: 'stretch',
                    borderColor: 'figma.grayscale.tier6',
                    mt: '14px',
                }}
            />
            {isLoading && (
                <Typography
                    variant="inherit"
                    sx={{
                        alignSelf: 'stretch',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 14,
                        color: 'figma.grayscale.tier2',
                        mt: 2,
                    }}
                >
                    Loading asset profile…
                </Typography>
            )}
            {!isLoading && (
                <Box
                    sx={{
                        mt: '24px',
                        mb: '24px',
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: '24px',
                        alignSelf: 'stretch',
                    }}
                >
                    {/* Top row: Name and Tags side by side */}
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '16px',
                            flexWrap: 'wrap',
                        }}
                    >
                        {/* Left: Name field */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="20x500"
                                sx={{
                                    color: 'figma.grayscale.textHier1',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: 18,
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '150%', // 27px
                                }}
                            >
                                Name
                            </Typography>
                            <TextField
                                value={editableName}
                                onChange={(e) => setEditableName(e.target.value)}
                                placeholder="Enter column name"
                                variant="outlined"
                                fullWidth
                                sx={{
                                    minWidth: '536px',
                                    '& .MuiOutlinedInput-root': {
                                        borderRadius: '12px',
                                        '& fieldset': {
                                            borderColor: 'figma.grayscale.stroke',
                                            borderWidth: '1px',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: 'figma.grayscale.stroke',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: 'figma.grayscale.stroke',
                                        },
                                    },
                                }}
                                InputProps={{
                                    sx: {
                                        fontFamily: '"DM Sans", sans-serif',
                                    },
                                }}
                            />
                        </Box>

                        {/* Right: Tags field */}
                        <Box
                            sx={{
                                flex: 1,
                                display: 'flex',
                                minWidth: '200px',
                                flexDirection: 'column',
                                gap: 1,
                            }}
                        >
                            <Typography
                                variant="18x500"
                                sx={{
                                    color: 'figma.grayscale.textHier1',
                                    fontFamily: '"DM Sans", sans-serif',
                                    fontSize: 18,
                                    fontStyle: 'normal',
                                    fontWeight: 500,
                                    lineHeight: '150%', // 27px
                                }}
                            >
                                Tags
                            </Typography>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexWrap: 'wrap',
                                    alignItems: 'center',
                                    gap: 1,
                                    mt: 0.5,
                                }}
                            >
                                {editableTags.map((tag) => (
                                    <Chip
                                        key={tag}
                                        label={tag}
                                        onDelete={() => handleRemoveTag(tag)}
                                        deleteIcon={
                                            <Box
                                                component="span"
                                                sx={{
                                                    width: 16,
                                                    height: 16,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    color: 'figma.grayscale.tier2',
                                                }}
                                            >
                                                <CloseIcon
                                                    style={{
                                                        width: 16,
                                                        height: 16,
                                                        color: 'figma.grayscale.tier2',
                                                    }}
                                                />
                                            </Box>
                                        }
                                        sx={{
                                            fontVariant: '16x400',
                                            display: 'flex',
                                            padding: '4px 12px',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            gap: '4px',
                                            borderRadius: '100px',
                                            bgcolor: 'figma.primary3',
                                            '& .MuiChip-label': {
                                                fontFamily: '"DM Sans", sans-serif',
                                                fontSize: 16,
                                                fontWeight: 400,
                                                lineHeight: '150%',
                                            },
                                        }}
                                    />
                                ))}
                                {isAddingTag ? (
                                    <Chip
                                        label={
                                            <InputBase
                                                value={newTagInput}
                                                onChange={(e) => setNewTagInput(e.target.value)}
                                                onKeyDown={handleNewTagKeyDown}
                                                onBlur={handleNewTagBlur}
                                                placeholder="Add tag"
                                                autoFocus
                                                sx={{
                                                    fontVariant: '16x400',
                                                    fontFamily: '"DM Sans", sans-serif',
                                                    fontSize: 16,
                                                    fontWeight: 400,
                                                    lineHeight: '150%',
                                                    px: 0,
                                                    py: 0,
                                                    width: 80,
                                                }}
                                            />
                                        }
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            borderRadius: '100px',
                                            bgcolor: 'figma.primary.3',
                                            height: 32,
                                            '& .MuiChip-label': {
                                                px: 0.75,
                                                py: 0,
                                            },
                                        }}
                                    />
                                ) : (
                                    <Box
                                        component="button"
                                        type="button"
                                        onClick={handleAddTag}
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            border: 'none',
                                            cursor: 'pointer',
                                            outline: 'none',
                                            padding: 0,
                                            background: 'transparent',
                                            color: 'figma.primary',
                                        }}
                                    >
                                        <AddIcon
                                            style={{
                                                width: 16,
                                                height: 16,
                                            }}
                                        />
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    </Box>

                    {/* Sections/Rows below Name/Tags */}
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            alignSelf: 'stretch',
                        }}
                    >
                        {orderedSections?.map((section, index) => {
                            const sectionRows = rows?.filter((row) => row.sectionId === section._id) ?? []
                            const isLast = index === orderedSections.length - 1

                            return (
                                <React.Fragment key={section._id}>
                                    <Box
                                        sx={{
                                            alignSelf: 'stretch',
                                        }}
                                    >
                                        <SectionEditComponent
                                            section={section}
                                            rows={sectionRows}
                                            cells={cellsForColumn}
                                            onCellChange={handleCellChange}
                                            onCellDelete={handleCellDelete}
                                        />
                                    </Box>
                                    {!isLast && (
                                        <Divider
                                            sx={{
                                                mt: 2,
                                                mb: 2,
                                                borderColor: 'figma.grayscale.tier6',
                                            }}
                                        />
                                    )}
                                </React.Fragment>
                            )
                        })}
                    </Box>
                </Box>
            )}
            <Box
                sx={{
                    mt: 3,
                    alignSelf: 'stretch',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: 2,
                }}
            >
                <Button
                    onClick={handleCancel}
                    sx={{
                        display: 'flex',
                        padding: '14px 24px',
                        alignItems: 'flex-start',
                        gap: '10px',
                        borderRadius: '14px',
                        border: '1px solid',
                        borderColor: 'figma.primary',
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 16,
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '150%', // 24px
                        color: 'figma.primary',
                        fontFeatureSettings: '"liga" off, "clig" off',
                        '&:hover': {
                            backgroundColor: 'rgba(0, 73, 234, 0.04)',
                            boxShadow: 'none',
                        },
                    }}
                >
                    Cancel
                </Button>
                <Button
                    onClick={handleSave}
                    sx={{
                        display: 'flex',
                        width: '113px',
                        padding: '14px 24px',
                        justifyContent: 'center',
                        alignItems: 'center',
                        gap: '10px',
                        borderRadius: '14px',
                        bgcolor: 'figma.primary',
                        boxShadow: 'none',
                        textTransform: 'uppercase',
                        fontFamily: '"DM Sans", sans-serif',
                        fontSize: 16,
                        fontStyle: 'normal',
                        fontWeight: 600,
                        lineHeight: '150%', // 24px
                        color: '#FFF',
                        fontFeatureSettings: '"liga" off, "clig" off',
                        '&:hover': {
                            bgcolor: 'figma.primary',
                            boxShadow: 'none',
                        },
                    }}
                >
                    Save
                </Button>
            </Box>
        </Dialog>
    )
}

export default ColumnEditView
